<script lang="ts" setup>
import { GroupState } from '@/composition/groups'
import { IUser } from '@/models/Users'
import { computed, onMounted, onUnmounted, onUpdated, ref } from 'vue'
import { UserState } from '../../../composition/users'
import UserProfilePopover from '../popover/UserProfilePopover.vue'
const props = defineProps({
  scrollData: {
    type: Object,
  },
})
const parentPosition = ref({
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  height: 0,
  width: 0,
  parentElementRect: new DOMRect(),
})
const position = ref({ left: 0, top: 0 })
const tooltipRef = ref<HTMLElement | null>(null)
const show = ref(false)
const user = ref<IUser>()
let shutDownTimer: NodeJS.Timeout | null = null

// this flags indicates if the content of tooltip is hovered, it is it hovered we do not want to close the tooltip
const isTooltipHovered = ref(false)

// this flag indicates if while fetching the data of user, the mouse was moved away from the parent element
// this is useful when user over the tag and before the data is fetched from the server, the mouse is moved away from the tag
const isHoverCancelled = ref(false)

const userRole = computed(() => {
  return GroupState.state.groupAdmins.get(user.value?.contactId)?.role
})

onMounted(async () => {
  tooltipRef.value = document.getElementById('user_profile_tooltip')

  if (tooltipRef.value) {
    tooltipRef.value.addEventListener('show-tooltip', handleShowTooltip)
    tooltipRef.value.addEventListener('close-tooltip', handleCloseTooltip)
  }
})

onUnmounted(() => {
  if (tooltipRef.value) {
    tooltipRef.value.removeEventListener('show-tooltip', handleShowTooltip)
    tooltipRef.value.removeEventListener('close-tooltip', handleCloseTooltip)
  }
})

async function handleShowTooltip(event: any) {
  try {
    // We cancel the previous timer if running and reset state. This handles the case where the user
    // moves mouse quickly between tags, which can mess up tooltip show/hide state since there is a
    // single shared instance of this component
    if (shutDownTimer) {
      clearTimeout(shutDownTimer)
    }
    show.value = false
    isHoverCancelled.value = false
    const resp = await UserState.getUserByContactId(event.detail.contactId)

    if (resp && !isHoverCancelled.value) {
      parentPosition.value = event.detail.parent
      user.value = resp
      show.value = true
    }
  } catch (e) {
    console.error(e)
  }
}

function showTooltip() {
  isTooltipHovered.value = true
}

function closeTooltip() {
  isTooltipHovered.value = false
  show.value = false
}

function handleCloseTooltip() {
  if (!show.value) {
    isHoverCancelled.value = true
  }
  shutDownTimer = setTimeout(() => {
    if (!isTooltipHovered.value) {
      show.value = false
    }
  }, 50)
}

onUpdated(async () => {
  const toolTipHeightOffset = 3
  tooltipRef.value = document.getElementById('user_profile_tooltip')

  const tooltipHeight = tooltipRef.value!.getBoundingClientRect().height

  const spaceAbove = parentPosition.value.top

  if (spaceAbove > tooltipHeight) {
    position.value.top =
      parentPosition.value.top -
      tooltipHeight +
      props.scrollData?.heightOffset -
      toolTipHeightOffset
  } else {
    position.value.top =
      parentPosition.value.bottom +
      props.scrollData?.heightOffset +
      toolTipHeightOffset
  }

  position.value.left =
    parentPosition.value.left + props.scrollData?.widthOffset
})
</script>
<template>
  <teleport to="body">
    <!-- do not change id of this element -->
    <UserProfilePopover
      v-show="show"
      id="user_profile_tooltip"
      class="tooltip w-[460px]"
      :user="user"
      :user-role="userRole"
      :show-badge="show"
      :style="{ top: position.top + 'px', left: position.left + 'px' }"
      @mouseover="showTooltip"
      @mouseleave="closeTooltip"
    />
  </teleport>
</template>

<style scoped>
.tooltip {
  position: absolute;
  word-break: break-word;
  z-index: 9999;
  box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12),
    0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
}
</style>
